// src/components/scenes/GalleryScene.js
// Filepath: // src/components/scenes/GalleryScene.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Fade,
  Card,
  CardContent,
  Paper,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import FastForwardIcon from "@mui/icons-material/FastForward";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import { fetchUserTvPrefs, DEFAULT_TV_PREFS } from "../../services/userPrefsService";
import { useScene } from "../../state/SceneContext";
import { setSceneHandlers } from "../../input/remote";
import SideInfoDrawer from "../common/SideInfoDrawer";
import { useKeepScreenOn } from "../../hooks/useKeepScreenOn";

const CONTROLS_IDLE_MS = 2500;
const TOAST_MS = 900;
const SEEK_STEP = 5;
const FIT_LS_KEY = "kbean_gallery_image_fit"; // "contain" | "cover"

/** TV-friendly sizes (px) for toasts and time */
const TV_FS = {
  toastLabel: 32,
  toastIcon: 64,
  timeFont: 28,
};

// Ramping thresholds in ms for x1, x2, x3, x4 while holding
const SEEK_RAMP_THRESHOLDS = {
  x2: 800,
  x3: 1600,
  x4: 2800,
};

// --- helpers --------------------------------------------------------------
const getExt = (u = "") => {
  try {
    const qless = String(u).split("?")[0];
    const m = qless.match(/\.([a-z0-9]+)$/i);
    return m ? m[1].toLowerCase() : "";
  } catch {
    return "";
  }
};

const VIDEO_EXTS = new Set(["mp4", "webm", "mov", "m3u8", "mpd"]);
const IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "avif"]);

const hasVideoExt = (u) => !!u && VIDEO_EXTS.has(getExt(u));
const hasImageExt = (u) => !!u && IMAGE_EXTS.has(getExt(u));

function pickFirstTruthy(arr) {
  for (const v of arr) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

function propFromAny(item, key) {
  if (!item) return undefined;
  const layers = [item, item.raw, item.data, item.asset, item._raw];
  for (const layer of layers) {
    if (layer && typeof layer[key] !== "undefined") return layer[key];
  }
  return undefined;
}

function formatTime(s) {
  if (!Number.isFinite(s)) return "0:00";
  const t = Math.max(0, Math.floor(s));
  const mm = Math.floor(t / 60);
  const ss = String(t % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

// --- hero metadata parity with ItemDetailScene ----------------------------
function computeBylineLikeItem(item) {
  if (item?.byline) return item.byline;
  const af =
    item?.artistFirstName ??
    item?.artistFirst ??
    item?._raw?.artistFirstName ??
    propFromAny(item, "artistFirstName") ??
    null;
  const al =
    item?.artistLastName ??
    item?.artistLast ??
    item?._raw?.artistLastName ??
    propFromAny(item, "artistLastName") ??
    null;
  const yr =
    item?.year ??
    item?.artYear ??
    item?._raw?.year ??
    item?._raw?.artYear ??
    propFromAny(item, "year") ??
    null;
  const name = [af, al].filter(Boolean).join(" ");
  return (name || yr) ? [name, yr].filter(Boolean).join(" • ") : null;
}
const getMedium = (item) => item?.medium ?? item?._raw?.medium ?? propFromAny(item, "medium") ?? null;
const getAllowWallpaper = (item) =>
  !!(item?.allowWallpaper ?? item?._raw?.allowWallpaper ?? propFromAny(item, "allowWallpaper") ?? false);
const getShares = (item) =>
  Number((item?.sharesYouOwn ?? item?._raw?.sharesYouOwn ?? propFromAny(item, "sharesYouOwn") ?? 0) || 0);
const getValue = (item) => item?.valueYouOwn ?? item?._raw?.valueYouOwn ?? propFromAny(item, "valueYouOwn") ?? null;
const getFullDescription = (item) =>
  item?.description ?? item?.subtitle ?? item?._raw?.artDescription ?? propFromAny(item, "artDescription") ?? "";

// --- slide inference (adds raw + hero-like fields) ------------------------
function inferSlide(item = {}) {
  const id =
    item.id ??
    item._id ??
    propFromAny(item, "id") ??
    propFromAny(item, "artworkId") ??
    String(Math.random());

  const title =
    item.title ||
    item.name ||
    propFromAny(item, "title") ||
    propFromAny(item, "name") ||
    "Untitled";

  const expected = propFromAny(item, "expectedMediaType");
  const itemType = propFromAny(item, "type");
  const expectImage = expected === "Image" || itemType === "Image" || itemType === "image";

  const mediaUrl = propFromAny(item, "mediaUrl");
  const videoUrlLegacy = propFromAny(item, "videoUrl");
  const srcUrl = propFromAny(item, "src");
  const url = propFromAny(item, "url");
  const hlsUrl = propFromAny(item, "hls") || propFromAny(item, "hlsUrl");
  const dashUrl = propFromAny(item, "dash") || propFromAny(item, "dashUrl");

  const imageUrlA = propFromAny(item, "imageUrl");
  const backgroundUrl = propFromAny(item, "backgroundUrl");
  const backdrop = propFromAny(item, "backdrop");
  const thumb = propFromAny(item, "thumbnailUrl") || propFromAny(item, "thumbnail");

  const videoCandidatesRaw = [
    hasVideoExt(mediaUrl) ? mediaUrl : null,
    videoUrlLegacy,
    hlsUrl,
    dashUrl,
    srcUrl,
    url,
  ].filter(Boolean);
  const videoCandidates = videoCandidatesRaw.filter((u) => hasVideoExt(u));

  const imageCandidatesRaw = [
    hasImageExt(mediaUrl) ? mediaUrl : null,
    imageUrlA,
    backgroundUrl,
    backdrop,
    thumb,
    url,
  ].filter(Boolean);
  const imageCandidates = imageCandidatesRaw.filter((u) => hasImageExt(u));

  let videoUrl = pickFirstTruthy(videoCandidates);
  let imageUrl = pickFirstTruthy(imageCandidates);
  if (!imageUrl && expectImage) {
    const fallbackImg = pickFirstTruthy([imageUrlA, backgroundUrl, backdrop, thumb]);
    if (fallbackImg) imageUrl = fallbackImg;
  }

  let type = "unknown";
  if (videoUrl) type = "video";
  else if (imageUrl) type = "image";

  const byline = computeBylineLikeItem(item);
  const medium = getMedium(item);
  const allowWallpaper = getAllowWallpaper(item);
  const shares = getShares(item);
  const value = getValue(item);
  const ownershipLabel =
    shares > 0
      ? `You own ${shares} ${shares === 1 ? "share" : "shares"}${value ? ` ($${value})` : ""}`
      : undefined;
  const description = getFullDescription(item);

  return {
    id,
    title,
    type,
    videoUrl,
    imageUrl,
    raw: item,
    byline,
    medium,
    allowWallpaper,
    shares,
    value,
    ownershipLabel,
    description,
  };
}

// Fisher–Yates shuffle
function shuffleInPlace(arr) {
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

export default function GalleryScene() {
  useKeepScreenOn(true);
  const { scene, setScene, popScene } = useScene();

  // Load prefs (random/minutes)
  const [prefs, setPrefs] = useState(DEFAULT_TV_PREFS);
  useEffect(() => {
    const stop = onAuthStateChanged(auth, async (user) => {
      try {
        if (user?.uid) {
          const p = await fetchUserTvPrefs(user.uid);
          setPrefs(p);
        } else {
          setPrefs(DEFAULT_TV_PREFS);
        }
      } catch (e) {
        console.error("[gallery] failed to load tv prefs:", e);
        setPrefs(DEFAULT_TV_PREFS);
      }
    });
    return () => stop();
  }, []);

  const startIndexParam = scene.params?.startIndex;
  const hasStartIndex = Number.isFinite(startIndexParam);

  const baseSlides = useMemo(() => {
    const items = Array.isArray(scene.params?.items) ? scene.params.items : [];
    return items.map(inferSlide).filter((s) => s.type !== "unknown");
  }, [scene.params?.items]);

  // Build ordered index list based on random preference
  const order = useMemo(() => {
    const n = baseSlides.length;
    const idx = Array.from({ length: n }, (_, i) => i);
    return prefs.galleryRandom ? shuffleInPlace(idx) : idx;
  }, [baseSlides.length, prefs.galleryRandom]);

  // Map original index -> position in ordered list
  const originalToOrdered = useMemo(() => {
    const map = new Map();
    order.forEach((origIdx, orderedPos) => map.set(origIdx, orderedPos));
    return map;
  }, [order]);

  const orderedSlides = useMemo(
    () => order.map((i) => baseSlides[i]),
    [order, baseSlides]
  );

  // Starting position logic
  const startPos = useMemo(() => {
    if (!orderedSlides.length) return 0;
    if (prefs.galleryRandom) return 0;
    if (hasStartIndex) {
      const mapped = originalToOrdered.get(startIndexParam) ?? 0;
      return Math.max(0, Math.min(mapped, orderedSlides.length - 1));
    }
    return 0;
  }, [
    orderedSlides.length,
    prefs.galleryRandom,
    hasStartIndex,
    originalToOrdered,
    startIndexParam,
  ]);

  const [pos, setPos] = useState(startPos);
  useEffect(() => {
    setPos(startPos);
  }, [startPos]);

  const totalSlides = orderedSlides.length;
  const hasMultipleSlides = totalSlides > 1;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [failedVideoAt, setFailedVideoAt] = useState(null);

  // Overlays auto-hide/show state
  const [uiOverlaysVisible, setUiOverlaysVisible] = useState(true);
  const uiHideTimer = useRef(null);

  // Toast for play/pause/seek (VideoPlayerScene style)
  const [toast, setToast] = useState(null); // { key, label, icon }
  const toastTimerRef = useRef(null);

  // Seek hold state for ramping RW/FF for video slides
  const seekHoldRef = useRef({
    active: false,
    dir: 0, // -1 rewind, 1 forward
    startTs: 0,
    lastMultiplier: 1,
  });

  // Image fit preference
  const [imageFit, setImageFit] = useState(() => {
    try {
      const v = localStorage.getItem(FIT_LS_KEY);
      return v === "cover" ? "cover" : "contain";
    } catch {
      return "contain";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(FIT_LS_KEY, imageFit);
    } catch { }
  }, [imageFit]);

  const videoRef = useRef(null);
  const currentSrcRef = useRef(null);

  // timers: both slideshow and video controls
  const timers = useRef({
    image: null,
    controlsHide: null,
    progress: null,
  });

  const current = orderedSlides[pos] || null;

  // Resolve a playable video src
  const resolvedVideoSrc = useMemo(() => {
    if (!current) return null;
    const direct =
      current.videoUrl || current?.raw?.mediaUrl || current?.raw?.videoUrl;
    if (hasVideoExt(direct)) return direct;
    const r = current.raw || {};
    const candidates = [
      r.hls,
      r.hlsUrl,
      r.dash,
      r.dashUrl,
      r.src,
      r.url,
    ].filter(Boolean);
    for (const c of candidates) {
      if (hasVideoExt(c)) return c;
    }
    return null;
  }, [current]);

  const isVideoCandidate =
    current?.type === "video" && !!resolvedVideoSrc;
  const isVideo = isVideoCandidate && failedVideoAt !== pos;
  const isImage =
    current?.type === "image" || (!isVideo && !!current?.imageUrl);

  // Top-right countdown state (images and videos)
  const [overlayProgress, setOverlayProgress] = useState(0);
  const [overlaySecondsLeft, setOverlaySecondsLeft] = useState(0);

  const clearTimers = useCallback(() => {
    if (timers.current.image) {
      clearTimeout(timers.current.image);
      timers.current.image = null;
    }
    if (timers.current.controlsHide) {
      clearTimeout(timers.current.controlsHide);
      timers.current.controlsHide = null;
    }
    if (timers.current.progress) {
      clearInterval(timers.current.progress);
      timers.current.progress = null;
    }
    if (uiHideTimer.current) {
      clearTimeout(uiHideTimer.current);
      uiHideTimer.current = null;
    }
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  }, []);

  const showToast = useCallback((label, icon) => {
    setToast({ key: `${label}-${Date.now()}`, label, icon });
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
    }, TOAST_MS);
  }, []);

  const cleanupVideoElement = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.removeAttribute("src");
      v.load?.();
      currentSrcRef.current = null;
    } catch { }
  }, []);

  const assignVideoSrc = useCallback((url) => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (!url) {
        v.removeAttribute("src");
        v.load?.();
        currentSrcRef.current = null;
        return;
      }
      const attr = v.getAttribute("src");
      if (attr !== url) {
        v.setAttribute("src", url);
        v.load?.();
        currentSrcRef.current = url;
      }
    } catch { }
  }, []);

  const tryPlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const activeSrc = v.getAttribute("src");
    if (!activeSrc || activeSrc !== currentSrcRef.current) return;

    try {
      const p = v.play?.();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          try {
            v.muted = true;
            v.play?.();
          } catch {
            setFailedVideoAt(pos);
          }
        });
      }
    } catch {
      try {
        v.muted = true;
        v.play?.();
      } catch {
        setFailedVideoAt(pos);
      }
    }
  }, [pos]);

  const next = useCallback(() => {
    cleanupVideoElement();
    setPos((i) =>
      orderedSlides.length ? (i + 1) % orderedSlides.length : 0
    );
  }, [cleanupVideoElement, orderedSlides.length]);

  const prev = useCallback(() => {
    cleanupVideoElement();
    setPos((i) =>
      orderedSlides.length
        ? (i - 1 + orderedSlides.length) % orderedSlides.length
        : 0
    );
  }, [cleanupVideoElement, orderedSlides.length]);

  // Video progress for bottom bar
  const [time, setTime] = useState({ current: 0, duration: 0 });

  // Video element listeners
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onEnded = () => next();
    const onLoaded = () => {
      if (v.getAttribute("src") !== currentSrcRef.current) return;
      const duration = Number.isFinite(v.duration) ? v.duration : 0;
      setTime((t) => ({ ...t, duration }));
      setOverlaySecondsLeft(duration - (v.currentTime || 0));
      setOverlayProgress(
        duration > 0
          ? ((v.currentTime || 0) / duration) * 100
          : 0
      );
      tryPlay();
    };
    const onTime = () => {
      if (v.getAttribute("src") !== currentSrcRef.current) return;
      const duration = Number.isFinite(v.duration) ? v.duration : 0;
      const currentT = Number.isFinite(v.currentTime)
        ? v.currentTime
        : 0;
      setTime({ current: currentT, duration });
      setOverlaySecondsLeft(Math.max(0, duration - currentT));
      setOverlayProgress(
        duration > 0
          ? Math.min(100, (currentT / duration) * 100)
          : 0
      );
    };
    const onError = () => setFailedVideoAt(pos);

    v.addEventListener("ended", onEnded);
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("canplay", onLoaded);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("error", onError);

    return () => {
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("canplay", onLoaded);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("error", onError);
    };
  }, [next, tryPlay, pos]);

  // image/video lifecycle + overlay scheduling
  const imageAdvanceMs = useMemo(() => {
    const m = Number(prefs.galleryMinutes);
    const minutes = Number.isFinite(m) && m > 0 ? m : 1;
    return minutes * 60 * 1000;
  }, [prefs.galleryMinutes]);

  useEffect(() => {
    clearTimers();

    // Initial overlay visibility; hide after 5s
    setUiOverlaysVisible(true);
    uiHideTimer.current = setTimeout(
      () => setUiOverlaysVisible(false),
      5000
    );

    setOverlayProgress(0);
    setOverlaySecondsLeft(0);

    if (isImage) {
      assignVideoSrc(null);

      // Single-image gallery: no auto-advance or countdown
      if (!hasMultipleSlides) {
        setOverlayProgress(0);
        setOverlaySecondsLeft(0);
        return;
      }

      const start = performance.now();

      setOverlayProgress(0);
      setOverlaySecondsLeft(imageAdvanceMs / 1000);

      timers.current.image = setTimeout(
        () => next(),
        imageAdvanceMs
      );

      timers.current.progress = setInterval(() => {
        const elapsed = performance.now() - start;
        const remainMs = Math.max(0, imageAdvanceMs - elapsed);
        setOverlayProgress(
          Math.min(100, (elapsed / imageAdvanceMs) * 100)
        );
        setOverlaySecondsLeft(remainMs / 1000);
      }, 100);

      return;
    }

    if (isVideo) {
      requestAnimationFrame(() => {
        assignVideoSrc(resolvedVideoSrc);
      });
    } else {
      assignVideoSrc(null);
    }
  }, [
    pos,
    isImage,
    isVideo,
    resolvedVideoSrc,
    next,
    clearTimers,
    assignVideoSrc,
    imageAdvanceMs,
    hasMultipleSlides,
  ]);

  // Fade overlays back in when 5s remain
  useEffect(() => {
    if (overlaySecondsLeft > 0 && overlaySecondsLeft <= 5) {
      setUiOverlaysVisible(true);
    }
  }, [overlaySecondsLeft]);

  // global cleanup
  useEffect(() => {
    return () => {
      clearTimers();
      cleanupVideoElement();
    };
  }, [clearTimers, cleanupVideoElement]);

  // Drawer scrolling
  const drawerScrollRef = useRef(null);
  const scrollDrawerBy = useCallback((delta) => {
    const el = drawerScrollRef.current;
    if (!el) return false;
    const prev = el.scrollTop;
    el.scrollTo({ top: prev + delta, behavior: "smooth" });
    return true;
  }, []);

  // --- Fire TV media key helpers (video only) -----------------------------

  // Only media keys here; DPAD left/right are kept for slide navigation.
  const getMediaSeekDirectionFromEvent = (e) => {
    const k = e.key;
    const code =
      typeof e.keyCode === "number" ? e.keyCode : e.which;

    if (k === "MediaRewind" || code === 227) return -1;
    if (k === "MediaFastForward" || code === 228) return 1;
    return 0;
  };

  const seekVideoBy = useCallback(
    (delta) => {
      if (!isVideo) return;
      const v = videoRef.current;
      if (!v) return;
      try {
        const next = (v.currentTime || 0) + delta;
        const max = v.duration || Infinity;
        v.currentTime = Math.max(0, Math.min(next, max));
      } catch { }
    },
    [isVideo]
  );

  const handleMediaSeekKeyDown = useCallback(
    (e) => {
      if (!isVideo) return false;

      const dir = getMediaSeekDirectionFromEvent(e);
      if (!dir) return false;

      const hold = seekHoldRef.current;
      const now =
        typeof e.timeStamp === "number" && e.timeStamp > 0
          ? e.timeStamp
          : Date.now();

      // Fresh press
      if (!e.repeat) {
        hold.active = true;
        hold.dir = dir;
        hold.startTs = now;
        hold.lastMultiplier = 1;

        seekVideoBy(dir * SEEK_STEP);

        showToast(
          "x1",
          dir > 0 ? (
            <FastForwardIcon sx={{ fontSize: TV_FS.toastIcon }} />
          ) : (
            <FastRewindIcon sx={{ fontSize: TV_FS.toastIcon }} />
          )
        );

        setControlsVisible(true);
        if (timers.current.controlsHide) {
          clearTimeout(timers.current.controlsHide);
        }
        timers.current.controlsHide = setTimeout(
          () => setControlsVisible(false),
          CONTROLS_IDLE_MS
        );

        return true;
      }

      // Hold and ramp
      if (!hold.active || hold.dir !== dir) {
        hold.active = true;
        hold.dir = dir;
        hold.startTs = now;
        hold.lastMultiplier = 1;
      }

      const elapsed = now - hold.startTs;
      let multiplier = 1;
      if (elapsed >= SEEK_RAMP_THRESHOLDS.x4) {
        multiplier = 4;
      } else if (elapsed >= SEEK_RAMP_THRESHOLDS.x3) {
        multiplier = 3;
      } else if (elapsed >= SEEK_RAMP_THRESHOLDS.x2) {
        multiplier = 2;
      }

      if (multiplier !== hold.lastMultiplier) {
        showToast(
          `x${multiplier}`,
          dir > 0 ? (
            <FastForwardIcon sx={{ fontSize: TV_FS.toastIcon }} />
          ) : (
            <FastRewindIcon sx={{ fontSize: TV_FS.toastIcon }} />
          )
        );
        hold.lastMultiplier = multiplier;
      }

      seekVideoBy(dir * SEEK_STEP * multiplier);

      setControlsVisible(true);
      if (timers.current.controlsHide) {
        clearTimeout(timers.current.controlsHide);
      }
      timers.current.controlsHide = setTimeout(
        () => setControlsVisible(false),
        CONTROLS_IDLE_MS
      );

      return true;
    },
    [isVideo, seekVideoBy, showToast]
  );

  const handleMediaSeekKeyUp = useCallback((e) => {
    const dir = getMediaSeekDirectionFromEvent(e);
    if (!dir) return false;
    const hold = seekHoldRef.current;
    if (hold.active && hold.dir === dir) {
      hold.active = false;
      hold.dir = 0;
      hold.startTs = 0;
      hold.lastMultiplier = 1;
      return true;
    }
    return false;
  }, []);

  // Play/Pause media key (Fire TV keyCode 179 / MediaPlayPause)
  const isPlayPauseKey = (e) => {
    const k = e.key;
    const code =
      typeof e.keyCode === "number" ? e.keyCode : e.which;
    if (k === "MediaPlayPause") return true;
    if (code === 179) return true;
    return false;
  };

  const togglePlay = useCallback(() => {
    if (!isVideo) return false;
    const v = videoRef.current;
    if (!v) return false;

    if (v.paused) {
      v
        .play()
        .then(() => {
          showToast(
            "Playing",
            <PlayArrowIcon sx={{ fontSize: TV_FS.toastIcon }} />
          );
        })
        .catch(() => { });
    } else {
      v.pause();
      showToast(
        "Paused",
        <PauseIcon sx={{ fontSize: TV_FS.toastIcon }} />
      );
    }
    return true;
  }, [isVideo, showToast]);

  const handlePlayPauseKeyDown = useCallback(
    (e) => {
      if (!isVideo) return false;
      if (!isPlayPauseKey(e)) return false;
      const handled = togglePlay();
      if (handled) {
        setControlsVisible(true);
        if (timers.current.controlsHide) {
          clearTimeout(timers.current.controlsHide);
        }
        timers.current.controlsHide = setTimeout(
          () => setControlsVisible(false),
          CONTROLS_IDLE_MS
        );
        return true;
      }
      return false;
    },
    [isVideo, togglePlay]
  );

  // DPAD bindings, keeping original semantics for Enter/Left/Right
  useEffect(() => {
    setSceneHandlers({
      // Raw key handlers: Fire TV media keys for video only
      onKeyDown: (e) => {
        if (handleMediaSeekKeyDown(e) === true) return true;
        if (handlePlayPauseKeyDown(e) === true) return true;
        return false;
      },
      onKeyUp: (e) => {
        if (handleMediaSeekKeyUp(e) === true) return true;
        return false;
      },

      onEnter: () => {
        // Same as original: toggles drawer, does not control playback
        setDrawerOpen((o) => !o);
        if (!drawerOpen) {
          setTimeout(() => {
            const el = drawerScrollRef.current;
            if (el) {
              el.setAttribute("tabindex", "-1");
              el.focus({ preventScroll: true });
            }
          }, 0);
        }
        return true;
      },
      onLeft: () => {
        // Same as original: close drawer or go to previous slide
        if (drawerOpen) {
          setDrawerOpen(false);
          return true;
        }
        if (!hasMultipleSlides) return true;
        prev();
        return true;
      },
      onRight: () => {
        // Same as original: close drawer or go to next slide
        if (drawerOpen) {
          setDrawerOpen(false);
          return true;
        }
        if (!hasMultipleSlides) return true;
        next();
        return true;
      },
      onDown: () => {
        if (drawerOpen) return scrollDrawerBy(+160);
        if (isImage) {
          setImageFit("contain");
          return true;
        }
        if (isVideo) {
          setControlsVisible(true);
          if (timers.current.controlsHide) {
            clearTimeout(timers.current.controlsHide);
          }
          timers.current.controlsHide = setTimeout(
            () => setControlsVisible(false),
            CONTROLS_IDLE_MS
          );
          return true;
        }
        return false;
      },
      onUp: () => {
        if (drawerOpen) return scrollDrawerBy(-160);
        if (isImage) {
          setImageFit("cover");
          return true;
        }
        if (controlsVisible) {
          setControlsVisible(false);
          if (timers.current.controlsHide) {
            clearTimeout(timers.current.controlsHide);
            timers.current.controlsHide = null;
          }
          return true;
        }
        return false;
      },
      onBack: () => {
        if (drawerOpen) {
          setDrawerOpen(false);
          return true;
        }
        clearTimers();
        cleanupVideoElement();
        const bt = scene.params?.backTo || null;
        if (bt && bt.name) {
          setScene({ name: bt.name, params: bt.params || {} });
        } else {
          popScene?.();
        }
        return true;
      },
    });

    const hold = seekHoldRef.current;

    return () => {
      setSceneHandlers({});
      if (hold) {
        hold.active = false;
        hold.dir = 0;
        hold.startTs = 0;
        hold.lastMultiplier = 1;
      }
    };
  }, [
    drawerOpen,
    controlsVisible,
    isVideo,
    isImage,
    hasMultipleSlides,
    next,
    prev,
    setScene,
    popScene,
    scene.params?.backTo,
    clearTimers,
    cleanupVideoElement,
    scrollDrawerBy,
    handleMediaSeekKeyDown,
    handleMediaSeekKeyUp,
    handlePlayPauseKeyDown,
  ]);

  if (!orderedSlides.length) {
    return (
      <Box
        className="scene"
        data-rails-root
        sx={{
          height: "100vh",
          width: "100vw",
          bgcolor: "black",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="h2">No media to show</Typography>
            <Typography sx={{ color: "text.secondary" }}>
              The gallery did not receive any items. Go back and try again.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Background style by image fit
  const imageBg =
    imageFit === "cover"
      ? `url(${current?.imageUrl}) center / cover no-repeat, black`
      : `url(${current?.imageUrl}) center / contain no-repeat, black`;

  const shouldShowCountdown =
    ((isImage && hasMultipleSlides) || isVideo) &&
    uiOverlaysVisible;

  return (
    <Box
      className="scene"
      data-rails-root
      sx={{
        position: "relative",
        height: "100vh",
        width: "100vw",
        bgcolor: "black",
        overflow: "hidden",
        isolation: "isolate",
      }}
      onMouseMove={() => {
        if (isVideo) {
          setControlsVisible(true);
          if (timers.current.controlsHide) {
            clearTimeout(timers.current.controlsHide);
          }
          timers.current.controlsHide = setTimeout(
            () => setControlsVisible(false),
            CONTROLS_IDLE_MS
          );
        }
      }}
    >
      {/* Persistent video element for video slides */}
      <video
        ref={videoRef}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: isVideo ? "block" : "none",
        }}
        autoPlay={false}
        preload="auto"
        playsInline
        controls={false}
        onClick={() => {
          if (isVideo) {
            const handled = togglePlay();
            if (handled) {
              setControlsVisible(true);
              if (timers.current.controlsHide) {
                clearTimeout(timers.current.controlsHide);
              }
              timers.current.controlsHide = setTimeout(
                () => setControlsVisible(false),
                CONTROLS_IDLE_MS
              );
            }
          }
        }}
      />

      {/* Image slide */}
      {isImage && (
        <Box
          key={`img-${current.id}-${pos}-${imageFit}`}
          sx={{
            position: "absolute",
            inset: 0,
            background: imageBg,
            transition:
              "background-position 220ms ease, background-size 220ms ease",
          }}
        />
      )}

      {/* Top-left title + count + instructions */}
      <Fade in={uiOverlaysVisible} timeout={250}>
        <Box
          sx={{
            position: "absolute",
            top: 24,
            left: 24,
            zIndex: 2,
            px: 1.5,
            py: 1,
            borderRadius: "12px",
            bgcolor: "rgba(0,0,0,0.55)",
            backdropFilter: "saturate(120%) blur(2px)",
            maxWidth: "72vw",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: "rgba(255,255,255,0.92)",
              whiteSpace: "normal",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
              hyphens: "auto",
              fontWeight: 800,
              fontSize: "clamp(22px, 2.6vw, 56px)",
              lineHeight: 1.1,
            }}
          >
            {current.title}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.85)",
              fontSize: "clamp(14px, 1.6vw, 22px)",
            }}
          >
            {pos + 1} / {totalSlides}
          </Typography>
          {isImage && (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.85)",
                mt: 0.25,
                fontSize: "clamp(13px, 1.5vw, 20px)",
              }}
            >
              Press Up to fill image. Press Down to fit.
            </Typography>
          )}
        </Box>
      </Fade>

      {/* Top-right countdown overlay */}
      {shouldShowCountdown && (
        <Fade in timeout={250}>
          <Box
            sx={{
              position: "absolute",
              top: 24,
              right: 24,
              zIndex: 2,
              width: 320,
              px: 1.5,
              py: 1,
              borderRadius: "12px",
              bgcolor: "rgba(0,0,0,0.55)",
              backdropFilter: "saturate(120%) blur(2px)",
            }}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.9)",
                display: "block",
                mb: 0.75,
                textAlign: "right",
                fontSize: "clamp(13px, 1.5vw, 20px)",
                fontWeight: 600,
              }}
            >
              {isImage ? "Next in" : "Ends in"}{" "}
              {formatTime(overlaySecondsLeft)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                Number.isFinite(overlayProgress)
                  ? Math.max(
                    0,
                    Math.min(100, overlayProgress)
                  )
                  : 0
              }
              sx={{
                height: 8,
                borderRadius: "999px",
                bgcolor: "rgba(255,255,255,0.18)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: "999px",
                },
              }}
            />
          </Box>
        </Fade>
      )}

      {/* Bottom controls overlay for video (VideoPlayerScene style) */}
      {isVideo && (
        <Fade in={controlsVisible}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              gridTemplateRows: "1fr auto",
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 50%, rgba(0,0,0,0.4) 100%)",
              }}
            />
            <Box sx={{ px: 10, pb: 3, alignSelf: "end" }}>
              <LinearProgress
                variant="determinate"
                value={
                  time.duration > 0
                    ? Math.min(
                      100,
                      (time.current / time.duration) * 100
                    )
                    : 0
                }
                sx={{
                  height: 8,
                  borderRadius: "999px",
                  bgcolor: "rgba(255,255,255,0.18)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: "999px",
                  },
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: `${TV_FS.timeFont}px`,
                    fontWeight: 800,
                    color: "rgba(255,255,255,0.95)",
                    textShadow:
                      "0 0 6px rgba(0,0,0,0.85), 0 0 2px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.6)",
                  }}
                >
                  {formatTime(time.current)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: `${TV_FS.timeFont}px`,
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.8)",
                    textShadow:
                      "0 0 6px rgba(0,0,0,0.85), 0 0 2px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.6)",
                  }}
                >
                  {formatTime(time.duration)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>
      )}

      {/* Center action toast (VideoPlayerScene style) */}
      <Fade in={!!toast}>
        <Box
          aria-hidden={!toast}
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            pointerEvents: "none",
            zIndex: 4,
          }}
        >
          {toast && (
            <Paper
              elevation={6}
              sx={{
                px: 3,
                py: 2,
                borderRadius: "16px",
                bgcolor: "rgba(0,0,0,0.78)",
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: 1.75,
              }}
            >
              {toast.icon}
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: `${TV_FS.toastLabel}px`,
                }}
              >
                {toast.label}
              </Typography>
            </Paper>
          )}
        </Box>
      </Fade>

      {/* Side Drawer */}
      <SideInfoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        anchor="right"
        width={900}
        title={current.title}
        subtitle={current.byline || undefined}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            borderRadius: "10px",
            overflow: "hidden",
            bgcolor: "grey.900",
          }}
        >
          <Box sx={{ pt: "56.25%" }} />
          <Box
            component="img"
            src={current.imageUrl || current.videoUrl}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              transform: "translateZ(0)",
            }}
          />
        </Box>
        
        {!!current.description && (
          <Box>
            <Typography variant="h2">Description</Typography>
          </Box>
        )}

        <Box
          ref={drawerScrollRef}
          tabIndex={-1}
          sx={{
            maxHeight: "42vh",
            overflowY: "auto",
            pr: 1,
            borderRadius: 1,
            outline: "none",
          }}
        >
          {!!current.description && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                sx={{
                  color: "text.secondary",
                  whiteSpace: "pre-wrap",
                  fontSize: "1.5rem",
                  lineHeight: 1.65,
                }}
              >
                {current.description}
              </Typography>
            </Box>
          )}
        </Box>
      </SideInfoDrawer>
    </Box>
  );
}
